import { useState, useRef } from "react";
import { motion, useInView, Variants, AnimatePresence } from "framer-motion";
import {
  FaGithub,
  FaLinkedin,
  FaInstagram,
  FaPaperPlane,
} from "react-icons/fa";
import { IoLogoWhatsapp } from "react-icons/io";
import { MdEmail, MdLocationOn } from "react-icons/md";

const Contact = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setLoading(true);
    setShowToast(false);

    try {
      const formData = new FormData(form);
      const response = await fetch("https://formspree.io/f/xzzgopny", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      setLoading(false);

      if (response.ok) {
        form.reset();
        setToastMsg("Message sent successfully!");
        setIsError(false);
      } else {
        const data = await response.json();
        setToastMsg(data.error || "Failed to send message. Please try again.");
        setIsError(true);
      }

      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    } catch (error) {
      setLoading(false);
      setToastMsg("Network error. Please try again later.");
      setIsError(true);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
      console.error("Form submission error:", error);
    }
  };

  return (
    <section
      id="contact"
      className="min-h-screen flex flex-col justify-center relative pt-24 pb-12 px-4 sm:px-6 lg:px-8 overflow-hidden"
      ref={ref}
    >
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
        <div className="absolute top-[10%] right-[10%] w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-[10%] left-[10%] w-96 h-96 bg-blue-500/10 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto w-full">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 mb-2 drop-shadow-sm">
            Get In Touch
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-cyan-400 to-blue-600 rounded-full mx-auto shadow-lg shadow-cyan-500/20" />
          <p className="text-gray-400 mt-4 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Have a project in mind or just want to say hi? I'd love to hear from
            you.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12"
        >
          {/* Contact Info Card */}
          <motion.div variants={itemVariants} className="flex flex-col gap-6">
            <div className="bg-gray-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8 relative overflow-hidden group hover:border-cyan-500/30 transition-all duration-500 h-full">
              <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                <MdEmail className="text-8xl text-cyan-400" />
              </div>

              <h3 className="text-2xl font-bold text-white mb-6">Let's Chat</h3>
              <p className="text-gray-400 mb-8 leading-relaxed">
                I'm currently available for freelance projects and full-time
                opportunities. Whether you have a question or just want to
                connect, feel free to reach out!
              </p>

              <div className="space-y-6">
                {/* Email Item */}
                <a
                  href="mailto:sai65265@gmail.com"
                  className="flex items-center gap-4 group/item"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover/item:border-cyan-500/50 group-hover/item:bg-cyan-500/10 transition-all duration-300">
                    <MdEmail className="text-xl text-gray-400 group-hover/item:text-cyan-400 transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-0.5">Email</p>
                    <p className="text-white font-medium group-hover/item:text-cyan-400 transition-colors">
                      sai65265@gmail.com
                    </p>
                  </div>
                </a>

                {/* WhatsApp Item */}
                <a
                  href="https://wa.link/oeaxld"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 group/item"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover/item:border-green-500/50 group-hover/item:bg-green-500/10 transition-all duration-300">
                    <IoLogoWhatsapp className="text-xl text-gray-400 group-hover/item:text-green-400 transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-0.5">WhatsApp</p>
                    <p className="text-white font-medium group-hover/item:text-green-400 transition-colors">
                      Chat on WhatsApp
                    </p>
                  </div>
                </a>

                {/* Location Item */}
                <div className="flex items-center gap-4 group/item">
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover/item:border-purple-500/50 group-hover/item:bg-purple-500/10 transition-all duration-300">
                    <MdLocationOn className="text-xl text-gray-400 group-hover/item:text-purple-400 transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-0.5">Location</p>
                    <p className="text-white font-medium group-hover/item:text-purple-400 transition-colors">
                      Remote / Worldwide
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Links Row */}
              <div className="flex gap-4 mt-8 pt-8 border-t border-white/10">
                <a
                  href="https://github.com/sainath-666"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:border-white/30 hover:scale-110 transition-all duration-300"
                  aria-label="GitHub"
                >
                  <FaGithub className="text-lg text-white" />
                </a>
                <a
                  href="https://linkedin.com/in/sainath666"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#0077b5] hover:border-[#0077b5] hover:scale-110 transition-all duration-300"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin className="text-lg text-white" />
                </a>
                <a
                  href="https://www.instagram.com/sainath_666/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-gradient-to-tr hover:from-yellow-400 hover:via-red-500 hover:to-purple-500 hover:border-transparent hover:scale-110 transition-all duration-300"
                  aria-label="Instagram"
                >
                  <FaInstagram className="text-lg text-white" />
                </a>
                <a
                  href="https://www.upwork.com/freelancers/sainathr8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#14a800] hover:border-[#14a800] hover:scale-110 transition-all duration-300"
                  aria-label="Upwork"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-4 h-4 fill-white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H7.828v7.112c-.002 1.406-1.141 2.546-2.547 2.546-1.405 0-2.543-1.14-2.545-2.546V3.492H0v7.112c0 2.914 2.37 5.303 5.281 5.303 2.913 0 5.283-2.389 5.283-5.303v-1.19c.529 1.107 1.182 2.229 1.974 3.221l-1.673 7.873h2.797l1.213-5.71c1.063.679 2.285 1.109 3.686 1.109 3 0 5.439-2.452 5.439-5.45 0-3-2.439-5.439-5.439-5.439z" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            variants={itemVariants}
            className="bg-gray-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8 relative overflow-hidden"
          >
            <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
              <h3 className="text-2xl font-bold text-white mb-2">
                Send Message
              </h3>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="text-sm font-medium text-gray-400 ml-1"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="John Doe"
                    autoComplete="name"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:bg-white/10 transition-all duration-300"
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-sm font-medium text-gray-400 ml-1"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    autoComplete="email"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:bg-white/10 transition-all duration-300"
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="message"
                    className="text-sm font-medium text-gray-400 ml-1"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    placeholder="Your message..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:bg-white/10 transition-all duration-300 resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full group relative px-8 py-4 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 hover:scale-[1.02] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <FaPaperPlane className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </>
                  )}
                </span>
              </button>
            </form>
          </motion.div>
        </motion.div>

        {/* Footer */}
        <motion.div
          variants={itemVariants}
          className="mt-20 pt-8 border-t border-white/10 text-center"
        >
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()}{" "}
            <span className="text-white font-medium">Sainathreddy Nerusupalli</span>. All
            rights reserved.
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Built with <span className="text-cyan-400">React.js</span> &{" "}
            <span className="text-cyan-400">Tailwind CSS</span>
          </p>
        </motion.div>
      </div>

      {/* Toast Notification */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: 20, x: "-50%" }}
            className={`fixed bottom-6 left-1/2 z-50 px-6 py-4 rounded-xl shadow-2xl backdrop-blur-md border flex items-center gap-3 ${
              isError
                ? "bg-red-500/10 border-red-500/50 text-red-200"
                : "bg-green-500/10 border-green-500/50 text-green-200"
            }`}
          >
            <div
              className={`w-2 h-2 rounded-full ${isError ? "bg-red-400" : "bg-green-400"}`}
            />
            <span className="font-medium">{toastMsg}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Contact;
